/**
 * Limites do transporte em tempo real. Mudar qualquer numero aqui muda o
 * contrato entre painel, API e daemon: faca bump de VERSAO_PROTOCOLO junto.
 */
// 2: PermissaoDecididaSchema virou união discriminada (o `permitir: boolean`
//    morreu) e o pedido carrega tipo/sugestões tipadas — daemon antigo não
//    saberia responder pergunta nem plano, então é recusado.
// 3: o eixo `motor` entra no fio (SdkIniciar, AgenteCota, SdkCapacidades) e as
//    chaves que nomeavam o CLI mudam de nome (sessionIdClaude -> idNoMotor,
//    uuidClaude -> uuidTurno). Zod DESCARTA chave desconhecida em silêncio, então
//    o daemon antigo não seria recusado: ficaria mandando o nome velho, e a linha
//    da sessão pararia em `pendente:` para sempre.
//    ATENÇÃO, e isto está no risco 9 do ADR-0017: subir este número NÃO RECUSA
//    NADA sozinho. O `EnvelopeSchema` é o único leitor de `v`, e ele não tem
//    consumidor fora do próprio spec. Quem recusa de verdade é a porta de versão
//    do `agente.ola` (`VERSAO_AGENTE_MINIMA`). Sem ela, este número é um
//    comentário se fazendo passar por barreira — o sétimo instrumento que mente.
export const VERSAO_PROTOCOLO = 3 as const;

/**
 * A versão mínima de daemon que a API aceita conectar. **Esta é a barreira de
 * verdade** — a `VERSAO_PROTOCOLO` acima é um número que ninguém confere.
 *
 * Sobe junto com toda quebra de forma no fio. Subir aqui derruba a frota inteira
 * para OFFLINE até cada máquina se atualizar, e isso é deliberado: é preferível
 * a metade da frota mandando o nome velho de um campo e as sessões parando em
 * `pendente:` sem ninguém entender por quê.
 */
export const VERSAO_AGENTE_MINIMA = '0.5.0' as const;

/**
 * O primeiro daemon que aceita anexo acima de 5 MiB. O anterior confere o teto
 * velho no `content-length` do download e recusa — mas só DEPOIS de a pessoa
 * subir o arquivo inteiro, e a linha de `anexos` de um PC nunca confirmada
 * prende o objeto na VPS para sempre. Por isso a API recusa no PREPARO
 * (`maquina_nao_aceita_anexo_grande`) quando a máquina da conversa é anterior.
 * Não é porta de conexão: daemon velho continua conectando e recebendo anexo
 * pequeno.
 */
export const VERSAO_AGENTE_ANEXO_GRANDE = '0.8.2' as const;

/** O teto de anexo que o daemon anterior a `VERSAO_AGENTE_ANEXO_GRANDE` aceita. */
export const ANEXO_BYTES_DO_DAEMON_ANTERIOR = 5 * 1024 * 1024;

export const LIMITES = {
  /** Texto de uma mensagem do usuario para a sessao. */
  textoMensagem: 65_536,
  /** Eventos por lote enviado pelo daemon. */
  loteEventos: 100,
  /** Bytes de um lote serializado. */
  loteBytes: 524_288,
  /** Linha crua do JSONL guardada em bruto; acima disso vira truncado. */
  brutoBytes: 262_144,
  /** Quadro binario do relay de midia. */
  frameBinario: 65_536,
  /** Creditos iniciais do relay de midia (janela de envio). */
  creditoInicial: 8,
  /** Eventos por pagina de replay. */
  paginaReplay: 500,
  /** Teto de eventos que o replay por WebSocket entrega antes de exigir recarga. */
  replayMaximo: 5_000,
  /** Anexos por mensagem e tamanho de cada um. */
  anexosPorMensagem: 5,
  /**
   * 5 GiB por anexo (pedido do dono, 2026-10-31; era 5 MiB).
   *
   * Não sobe `VERSAO_PROTOCOLO`, contra o que diz o cabeçalho, e por escrito:
   * o número sozinho não recusa ninguém (o risco 9 acima), e subir o teto não
   * muda forma nenhuma no fio. O daemon anterior continua conferindo 5 MiB no
   * `content-length` do download e RECUSA o anexo maior com erro — o envio
   * volta como falha, nunca como arquivo truncado.
   *
   * O que o número grande exige fora daqui: a API não segura o POST da mensagem
   * esperando a máquina baixar (`ENTREGA_EM_SEGUNDO_PLANO_BYTES`), o
   * `requestTimeout` do Node e o `read_timeout` do Caddy cobrem o `PUT`, e o
   * disco da VPS — que é compartilhado com a produção de terceiros — tem
   * reserva conferida antes de emitir o link.
   */
  anexoBytes: 5 * 1024 * 1024 * 1024,
} as const;

/**
 * Acima desta SOMA de anexos numa mensagem, a API não segura o POST do painel
 * esperando a máquina: responde na hora com o id da entrega e avisa o fim pelo
 * evento `sessao.entrega` (`SessaoEntregaSchema`, em `painel.ts`).
 *
 * O porquê: o daemon baixa TODOS os anexos antes de responder ao `sdk.enviar`
 * (`conexao/cliente.ts`), e a API esperava esse ack por `INTERVALOS.ackMs`
 * (10 s). Com 5 MiB cabia; com 1 GB o painel dizia "a máquina não recebeu"
 * enquanto ela ainda baixava — e a mensagem chegava depois, levando a pessoa a
 * mandar de novo. Abaixo deste número o caminho síncrono continua igual.
 */
export const ENTREGA_EM_SEGUNDO_PLANO_BYTES = 32 * 1024 * 1024;

/**
 * Ritmo MÍNIMO que a API assume para a máquina baixar os anexos (bytes/s). Dá
 * o prazo do ack do `sdk.enviar`/`sessao.mensagem_local` com anexos:
 * `prazoDaEntregaMs`. 1 MiB/s (~8 Mbps) é folgado para a fibra da casa e ainda
 * acaba com uma máquina morta em tempo finito.
 */
export const RITMO_MINIMO_DA_ENTREGA_BYTES_S = 1024 * 1024;

/** Prazo do ack de uma mensagem cujos anexos somam `totalBytes`. */
export function prazoDaEntregaMs(totalBytes: number): number {
  return INTERVALOS.ackMs + Math.ceil(Math.max(0, totalBytes) / RITMO_MINIMO_DA_ENTREGA_BYTES_S) * 1000;
}

/**
 * Tetos do canal da sessão mestre (F13). Constante NOVA: acréscimo não sobe
 * `VERSAO_PROTOCOLO`. Os tetos de despacho são barreira determinística contra
 * injeção — em modo `auto` não há cartão de permissão, então quem segura é o
 * handler, e o handler segura por estes números.
 */
export const LIMITES_MESTRE = {
  /**
   * Bytes do `dados` serializado de uma resposta de consulta (R9: era 60 mil;
   * 10 eventos de `ler_conversa` a 2.000 caracteres cabem em ~20 mil).
   */
  respostaBytes: 24_000,
  /**
   * Eventos por leitura de `ler_conversa` (R9: era 80). Esta é a única consulta
   * que tem cursor (`antesDeSeq`) para ir além; `listar_sessoes` e `buscar` não
   * têm, e por isso seus tetos NÃO baixaram. A API aplica o teto também a quem
   * pede mais (daemon anterior ainda aceita até 200): clampa, não recusa.
   */
  conversaPorLeitura: 10,
  /** Corte de texto por evento em `conversa.ler`. */
  textoPorEvento: 2_000,
  /** Por arquivo e total da leitura curada do `.claude`. */
  configArquivoBytes: 24_000,
  configTotalBytes: 120_000,
  /** Despachos (`mestre.mensagem` + `mestre.criar_sessao`) por turno do mestre. */
  despachosPorTurno: 5,
  /** Despachos para o MESMO alvo por hora. */
  despachosPorAlvoHora: 12,
  /**
   * Recados de um mestre para o OUTRO mestre, por hora.
   *
   * Teto PRÓPRIO, e não `despachosPorAlvoHora`, porque o risco é de outra
   * natureza: mestre→mestre **se autoalimenta**. Um recado acorda o par, que
   * responde, que acorda o primeiro — e em modo `auto` não existe cartão de
   * permissão para nenhum dos dois, então não há humano no laço em ponto
   * nenhum. O teto por hora é o único freio determinístico que sobra.
   *
   * Menor que os 12 do alvo comum de propósito: falar com uma sessão de
   * trabalho doze vezes numa hora é uso; falar com o par doze vezes é os dois
   * conversando entre si em vez de trabalharem. Quatro cobre o caso legítimo —
   * "isto é do seu motor, assuma" — com folga para o par responder.
   */
  recadosAoParPorHora: 4,
  /**
   * Chamadas `mestre.*` por minuto, somadas. Subiu de 30 quando pendências e
   * vigília entraram: um recado de acompanhamento honesto custa 3-4 leituras.
   */
  chamadasPorMinuto: 60,
  /**
   * Teto de um arquivo que a mestre envia para a conversa (`enviar_arquivo`).
   *
   * Constante PRÓPRIA, e não `LIMITES.anexoBytes`: mudar qualquer número do
   * `LIMITES` muda o contrato dos três lados e exige bump de
   * `VERSAO_PROTOCOLO` (está escrito no cabeçalho daquele arquivo). Acrescentar
   * aqui não exige nada, que é a mesma razão pela qual `LIMITES_MESTRE` nasceu.
   *
   * 10 MiB (era o dobro do que entrava, até o anexo subir para 5 GiB em
   * 2026-10-31 — os dois tetos são independentes). O caso que pediu o número é um PDF de
   * apresentação, que costuma ficar entre 2 e 15 MB; abaixo disso a ferramenta
   * seria inútil justamente para o arquivo que a motivou. Acima, recusa com
   * erro claro — **nunca truncar**: meio PDF entregue como PDF inteiro é o
   * nono instrumento que mente, e já bastam oito.
   *
   * Este número é conservador de propósito enquanto o armazenamento não tiver
   * faxina própria: hoje NADA apaga o objeto (a poda de 48 h do daemon é da
   * cópia local), e o disco da VPS anda em ~79%.
   */
  arquivoEnviadoBytes: 10 * 1024 * 1024,
} as const;

export const INTERVALOS = {
  /** Pulso do daemon para a API. */
  pulsoMs: 15_000,
  /** Sem pulso por este tempo, a maquina vira OFFLINE. */
  pulsoToleranciaMs: 45_000,
  /** Coalescencia de lotes e deltas. */
  coalescenciaMs: 50,
  /** Timeout de comandos com ack. */
  ackMs: 10_000,
  /** Varredura de sessoes vivas na maquina. */
  descobertaMs: 5_000,
  /** Polling do tail de JSONL. */
  tailMs: 500,
} as const;
